import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

function Invite() {
  const [activeTab, setActiveTab] = useState('lead');
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'content manager',
    approvalRights: false
  });
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    companyname: '',
    gst: '',
    city: '',
    state: '',
    pincode: '',
    planId: '',
    interestedCategories: [],
    tenure: '',
    validityTo: ''
  });
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const categoryDropdownRef = useRef(null);

  // Fetch active subscriptions
  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (!API_URL) return;
      setIsLoadingSubscriptions(true);
      try {
        const response = await axios.post(
          API_URL,
          { action: 'getActiveSubscriptions' },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const data = response.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.subscriptions)
              ? data.subscriptions
              : data
                ? [data]
                : [];
        setSubscriptions(list.filter(Boolean));
      } catch (error) {
        console.error('Failed to load subscriptions:', error);
      } finally {
        setIsLoadingSubscriptions(false);
      }
    };

    fetchSubscriptions();
  }, []);

  // Fetch active categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (!API_URL) return;
      setIsLoadingCategories(true);
      try {
        const response = await axios.post(
          API_URL,
          { action: 'getActiveCategories' },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const list = Array.isArray(response.data) ? response.data : [];
        setCategories(list.filter(Boolean));
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCategoryDropdownOpen]);

  const selectedSubscription = useMemo(
    () =>
      subscriptions.find(
        (s) =>
          String(s._id || s.subscriptionId || '') === String(userForm.planId)
      ),
    [subscriptions, userForm.planId]
  );

  /** Today + N calendar months → YYYY-MM-DD (local) */
  const todayLocalYmd = () => {
    const t = new Date();
    const y = t.getFullYear();
    const mo = String(t.getMonth() + 1).padStart(2, '0');
    const day = String(t.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  };

  const addMonthsFromToday = (monthsToAdd) => {
    const n = Number(monthsToAdd);
    if (!Number.isFinite(n) || n < 1) return '';
    const d = new Date();
    d.setMonth(d.getMonth() + n);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  };

  const formatValidityToDisplay = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(`${isoDate}T12:00:00`);
    if (Number.isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPlanMoney = (value, currency = 'INR') => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const formatted = n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currency || 'INR'} ${formatted}`;
  };

  const handlePlanSelect = (e) => {
    const planId = e.target.value;
    if (!planId) {
      setUserForm((prev) => ({
        ...prev,
        planId: '',
        tenure: '',
        validityTo: ''
      }));
      return;
    }
    const sub = subscriptions.find(
      (s) => String(s._id || s.subscriptionId || '') === String(planId)
    );
    const months = sub?.durationMonths;
    const tenureLabel =
      months != null && Number.isFinite(Number(months))
        ? `${Number(months)} Months`
        : '';
    const validityTo =
      months != null && Number.isFinite(Number(months))
        ? addMonthsFromToday(Number(months))
        : '';
    setUserForm((prev) => ({
      ...prev,
      planId,
      tenure: tenureLabel,
      validityTo
    }));
  };

  const sanitizePhoneInput = (value) =>
    String(value).replace(/\D/g, '').slice(0, 10);

  const handleLeadChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validation for name field - no numeric values
    if (name === 'name' && /\d/.test(value)) {
      toast.warning('Name cannot contain numbers');
      return;
    }

    if (name === 'phone') {
      setLeadForm((prev) => ({ ...prev, phone: sanitizePhoneInput(value) }));
      return;
    }
    
    setLeadForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    
    // Validation for name field - no numeric values
    if (name === 'name' && /\d/.test(value)) {
      toast.warning('Name cannot contain numbers');
      return;
    }
    
    // Validation for city field - no numeric values
    if (name === 'city' && /\d/.test(value)) {
      toast.warning('City cannot contain numbers');
      return;
    }
    
    // Validation for state field - no numeric values
    if (name === 'state' && /\d/.test(value)) {
      toast.warning('State cannot contain numbers');
      return;
    }
    
    // Validation for pincode field - only numeric values
    if (name === 'pincode' && /[a-zA-Z]/.test(value)) {
      toast.warning('Pincode can only contain numbers');
      return;
    }

    if (name === 'phone') {
      setUserForm((prev) => ({ ...prev, phone: sanitizePhoneInput(value) }));
      return;
    }
    
    setUserForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setUserForm((prev) => {
      const exists = prev.interestedCategories.includes(categoryId);
      const updated = exists
        ? prev.interestedCategories.filter((id) => id !== categoryId)
        : [...prev.interestedCategories, categoryId];
      return { ...prev, interestedCategories: updated };
    });
  };

  const toggleCategoryDropdown = () => {
    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
  };

  const getSelectedCategoriesText = () => {
    if (userForm.interestedCategories.length === 0) {
      return 'Select min 3 categories';
    }
    return `${userForm.interestedCategories.length} selected`;
  };

  const handleLeadSubmit = async () => {
    if (!leadForm.name.trim() || !leadForm.email.trim() || !leadForm.phone.trim()) {
      toast.error('Please fill name, email, and phone.');
      return;
    }
    if (!/^\d{10}$/.test(leadForm.phone)) {
      toast.error('Phone must be exactly 10 digits.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        action: 'inviteLeadManager',
        name: leadForm.name.trim(),
        email: leadForm.email.trim(),
        phone: leadForm.phone.trim(),
        role: leadForm.role,
        approvalRights: leadForm.approvalRights
      };

      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const result = response.data;
      if (result?.success || result?.message) {
        toast.success(result?.message || 'Invite sent successfully');
        setLeadForm({
          name: '',
          email: '',
          phone: '',
          role: 'contentManager',
          approvalRights: false
        });
      } else {
        toast.error(result?.message || 'Failed to send invite');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Invite failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserSubmit = async () => {
    if (!userForm.name.trim() || !userForm.email.trim() || !userForm.phone.trim()) {
      toast.error('Please fill name, email, and phone.');
      return;
    }
    if (!/^\d{10}$/.test(userForm.phone)) {
      toast.error('Phone must be exactly 10 digits.');
      return;
    }
    if (!userForm.companyname.trim()) {
      toast.error('Company name is required.');
      return;
    }
    if (!userForm.city.trim() || !userForm.state.trim() || !userForm.pincode.trim()) {
      toast.error('Please fill city, state, and pincode.');
      return;
    }
    if (!userForm.planId) {
      toast.error('Please select a plan.');
      return;
    }
    if (userForm.interestedCategories.length < 3) {
      toast.error('Please select at least 3 interested categories.');
      return;
    }
    if (!userForm.tenure) {
      toast.error('Please select a plan (duration is required).');
      return;
    }
    if (!userForm.validityTo) {
      toast.error('Validity end date could not be set. Choose a plan with a duration.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        action: 'inviteUser',
        name: userForm.name.trim(),
        email: userForm.email.trim(),
        phone: userForm.phone.trim(),
        role:"user",
        companyname: userForm.companyname.trim(),
        gst: userForm.gst.trim(),
        city: userForm.city.trim(),
        state: userForm.state.trim(),
        pincode: userForm.pincode.trim(),
        planId: userForm.planId,
        interestedCategories: userForm.interestedCategories,
        tenure: userForm.tenure,
        validityTo: userForm.validityTo,
        validityFrom: todayLocalYmd()
      };

      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const result = response.data;
      if (result?.success || result?.message) {
        toast.success(result?.message || 'User invite sent successfully');
        setUserForm({
          name: '',
          email: '',
          phone: '',
          companyname: '',
          gst: '',
          city: '',
          state: '',
          pincode: '',
          planId: '',
          interestedCategories: [],
          tenure: '',
          validityTo: ''
        });
      } else {
        toast.error(result?.message || 'Failed to send invite');
      }
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Invite failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Invite</h1>
            <p>Manage Lead Manager and User invites.</p>
          </div>
          <div className="col-sm-6 tabs-container">
            <ul className="nav nav-tabs employee-tabs-list">
              <li className="nav-item">
                <button
                  className={`nav-link employee-tab-link ${activeTab === 'lead' ? 'active' : ''}`}
                  onClick={() => setActiveTab('lead')}
                  type="button"
                >
                  Lead Manager
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link employee-tab-link ${activeTab === 'user' ? 'active' : ''}`}
                  onClick={() => setActiveTab('user')}
                  type="button"
                >
                  User
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="card employee-content-card" style={{ overflow: 'visible' }}>
        <div className="card-body" style={{ overflow: 'visible' }}>
          {activeTab === 'lead' ? (
            <div className="row">
              <div className="col-md-4 form-group">
                <label htmlFor="lead-name" className="form-label">Name</label>
                <input
                  id="lead-name"
                  name="name"
                  type="text"
                  className="form-control"
                  placeholder="Enter name"
                  value={leadForm.name}
                  onChange={handleLeadChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="lead-email" className="form-label">Email</label>
                <input
                  id="lead-email"
                  name="email"
                  type="email"
                  className="form-control"
                  placeholder="Enter email"
                  value={leadForm.email}
                  onChange={handleLeadChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="lead-phone" className="form-label">Phone</label>
                <input
                  id="lead-phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  className="form-control"
                  placeholder="10-digit number"
                  maxLength={10}
                  value={leadForm.phone}
                  onChange={handleLeadChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="lead-role" className="form-label">Role</label>
                <select
                  id="lead-role"
                  name="role"
                  className="form-control"
                  value={leadForm.role}
                  onChange={handleLeadChange}
                >
                  <option value="admin">Admin</option>
                  <option value="content manager">Content Manager</option>
                </select>
              </div>
              <div className="col-md-4 form-group">
                <label className="form-label">Approval Rights</label>
                <div>
                  <label className="switch-toggle">
                    <input
                      id="lead-approval"
                      name="approvalRights"
                      type="checkbox"
                      checked={leadForm.approvalRights}
                      onChange={handleLeadChange}
                    />
                    <span className="switch-slider" data-on="On" data-off="Off" />
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="row" style={{ overflow: 'visible' }}>
              <div className="col-md-4 form-group">
                <label htmlFor="user-name" className="form-label">
                  Name <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-name"
                  name="name"
                  type="text"
                  className="form-control"
                  placeholder="Enter name"
                  value={userForm.name}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-email" className="form-label">
                  Email <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-email"
                  name="email"
                  type="email"
                  className="form-control"
                  placeholder="Enter email"
                  value={userForm.email}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-phone" className="form-label">
                  Phone <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  className="form-control"
                  placeholder="10-digit number"
                  maxLength={10}
                  value={userForm.phone}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-company" className="form-label">
                  Company Name <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-company"
                  name="companyname"
                  type="text"
                  className="form-control"
                  placeholder="Enter company"
                  value={userForm.companyname}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-gst" className="form-label">GST</label>
                <input
                  id="user-gst"
                  name="gst"
                  type="text"
                  className="form-control"
                  placeholder="Enter GST"
                  value={userForm.gst}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-city" className="form-label">
                  City <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-city"
                  name="city"
                  type="text"
                  className="form-control"
                  placeholder="Enter city"
                  value={userForm.city}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-state" className="form-label">
                  State <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-state"
                  name="state"
                  type="text"
                  className="form-control"
                  placeholder="Enter state"
                  value={userForm.state}
                  onChange={handleUserChange}
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-pincode" className="form-label">
                  Pincode <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-pincode"
                  name="pincode"
                  type="text"
                  className="form-control"
                  placeholder="Enter pincode"
                  value={userForm.pincode}
                  onChange={handleUserChange}
                />
              </div>

              <div className="col-md-4 form-group">
                <label htmlFor="user-plan" className="form-label">
                  Plan <span className="required-asterisk">*</span>
                </label>
                {isLoadingSubscriptions ? (
                  <div style={{ padding: '10px 0' }}>
                    <LoadingIndicator size={30} className="inline-loader" />
                  </div>
                ) : (
                  <select
                    id="user-plan"
                    name="planId"
                    className="form-control"
                    value={userForm.planId}
                    onChange={handlePlanSelect}
                  >
                    <option value="">Select Plan</option>
                    {subscriptions
                      .filter((sub) => sub._id || sub.subscriptionId)
                      .map((sub) => {
                        const sid = sub._id || sub.subscriptionId;
                        const name = sub.planName || sub.plan || 'Plan';
                        const mo = sub.durationMonths;
                        const moLabel =
                          mo != null && Number.isFinite(Number(mo))
                            ? `${Number(mo)} mo`
                            : '';
                        return (
                          <option key={String(sid)} value={String(sid)}>
                            {moLabel ? `${name} (${moLabel})` : name}
                          </option>
                        );
                      })}
                  </select>
                )}
                {!isLoadingSubscriptions && subscriptions.length === 0 && (
                  <small className="text-muted d-block mt-1">
                    No active subscription plans. Add plans in Subscription.
                  </small>
                )}
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-duration" className="form-label">
                  Duration
                </label>
                <input
                  id="user-duration"
                  type="text"
                  className="form-control"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                  placeholder="Select a plan"
                  value={
                    selectedSubscription &&
                    selectedSubscription.durationMonths != null
                      ? `${selectedSubscription.durationMonths} months`
                      : ''
                  }
                />
              </div>
              <div className="col-md-4 form-group">
                <label htmlFor="user-plan-amount" className="form-label">
                  Total amount
                </label>
                <input
                  id="user-plan-amount"
                  type="text"
                  className="form-control"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                  placeholder="Select a plan"
                  value={
                    selectedSubscription
                      ? formatPlanMoney(
                          selectedSubscription.totalAmount ??
                            selectedSubscription.amount,
                          selectedSubscription.currency
                        )
                      : ''
                  }
                />
                {selectedSubscription &&
                  selectedSubscription.amount != null &&
                  selectedSubscription.totalAmount != null &&
                  Number(selectedSubscription.amount) !==
                    Number(selectedSubscription.totalAmount) && (
                    <small className="text-muted d-block mt-1">
                      Base:{' '}
                      {formatPlanMoney(
                        selectedSubscription.amount,
                        selectedSubscription.currency
                      )}
                    </small>
                  )}
              </div>

              

              <div className="col-md-4 form-group">
                <label htmlFor="user-validity-to" className="form-label">
                  Validity to <span className="required-asterisk">*</span>
                </label>
                <input
                  id="user-validity-to"
                  type="date"
                  className="form-control"
                  readOnly
                  style={{ backgroundColor: '#f8f9fa', cursor: 'default' }}
                  value={userForm.validityTo}
                  title={
                    userForm.validityTo
                      ? formatValidityToDisplay(userForm.validityTo)
                      : ''
                  }
                />
                {/* <small className="text-muted d-block mt-1">
                  {userForm.validityTo
                    ? `Ends ${formatValidityToDisplay(userForm.validityTo)} (today + plan duration)`
                    : 'Select a plan to set the end date.'}
                </small> */}
              </div>

              <div className="col-md-4 form-group" style={{ marginBottom: '20px', overflow: 'visible', position: 'relative', zIndex: 1 }}>
                <label className="form-label">
                  Interested Categories <span className="required-asterisk">*</span>
                  <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                    (Select minimum 3 categories)
                  </span>
                </label>
                {isLoadingCategories ? (
                  <div >
                    <LoadingIndicator size={30} className="inline-loader" />
                  </div>
                ) : (
                  <div style={{ position: 'relative', zIndex: 10 }} ref={categoryDropdownRef}>
                    <div
                      className="form-control"
                      onClick={toggleCategoryDropdown}
                      style={{
                        cursor: 'pointer',
                        paddingRight: '30px'
                      }}
                    >
                      {getSelectedCategoriesText()}
                    </div>
                    <i 
                      className={`fas fa-chevron-${isCategoryDropdownOpen ? 'up' : 'down'}`}
                      style={{ 
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#6b7280',
                        fontSize: '12px',
                        pointerEvents: 'none'
                      }}
                    ></i>
                    {isCategoryDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: '#fff',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        maxHeight: '200px',
                        minHeight: '100px',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                        zIndex: 9999,
                        WebkitOverflowScrolling: 'touch'
                      }}>
                        {categories.length === 0 ? (
                          <div style={{ 
                            padding: '16px', 
                            textAlign: 'center', 
                            color: '#6b7280',
                            fontSize: '14px'
                          }}>
                            No categories available
                          </div>
                        ) : (
                          <div>
                            {categories.map((cat, index) => (
                              <label
                                key={cat._id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  backgroundColor: userForm.interestedCategories.includes(cat._id) ? '#eff6ff' : '#fff',
                                  borderBottom: index < categories.length - 1 ? '1px solid #f3f4f6' : 'none',
                                  transition: 'background-color 0.15s ease',
                                  margin: 0,
                                  minHeight: '48px'
                                }}
                                onMouseEnter={(e) => {
                                  if (!userForm.interestedCategories.includes(cat._id)) {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!userForm.interestedCategories.includes(cat._id)) {
                                    e.currentTarget.style.backgroundColor = '#fff';
                                  } else {
                                    e.currentTarget.style.backgroundColor = '#eff6ff';
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={userForm.interestedCategories.includes(cat._id)}
                                  onChange={() => handleCategoryToggle(cat._id)}
                                  style={{ 
                                    marginRight: '12px',
                                    width: '18px',
                                    height: '18px',
                                    cursor: 'pointer',
                                    accentColor: '#3b82f6',
                                    flexShrink: 0
                                  }}
                                />
                                <span style={{ 
                                  fontSize: '14px', 
                                  userSelect: 'none',
                                  flex: 1,
                                  color: '#1f2937'
                                }}>
                                  {cat.category}
                                </span>
                                {userForm.interestedCategories.includes(cat._id) && (
                                  <i className="fas fa-check" style={{ 
                                    marginLeft: '8px', 
                                    color: '#3b82f6',
                                    fontSize: '14px',
                                    flexShrink: 0
                                  }}></i>
                                )}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
              </div>
              
            </div>
          )}

          <div className="form-actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={activeTab === 'lead' ? handleLeadSubmit : handleUserSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <LoadingIndicator size={22} className="inline-loader" />
                  <span style={{ marginLeft: '8px' }}>Sending...</span>
                </>
              ) : (
                'Send Invite'
              )}
            </button>
            <button className="btn btn-secondary" type="button">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Invite;
